import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

// Binder Images
import BabyBlueBinder from '../../assets/images/Binders/BabyBlueBinder.png';
import BlueBinder from '../../assets/images/Binders/BlueBinder.png';
import GreenBinder from '../../assets/images/Binders/GreenBinder.png';
import MintGreenBinder from '../../assets/images/Binders/MintGreenBinder.png';
import OrangeBinder from '../../assets/images/Binders/OrangeBinder.png';
import PinkBinder from '../../assets/images/Binders/PinkBinder.png';
import PurpleBinder from '../../assets/images/Binders/PurpleBinder.png';
import RedBinder from '../../assets/images/Binders/RedBinder.png';
import WhiteBinder from '../../assets/images/Binders/WhiteBinder.png';
import YellowBinder from '../../assets/images/Binders/YellowBinder.png';
import { useHideNavWhile } from '../BottomSheet';
import { isReservedBinderName, RESERVED_BINDER_NAME_ERROR } from '../../services/tradeBinder';

const binderOptions = [
  // Row 1: Warm colors + neutrals
  { id: 'red', image: RedBinder },
  { id: 'orange', image: OrangeBinder },
  { id: 'yellow', image: YellowBinder },
  { id: 'pink', image: PinkBinder },
  { id: 'white', image: WhiteBinder },
  // Row 2: Cool colors
  { id: 'green', image: GreenBinder },
  { id: 'mintgreen', image: MintGreenBinder },
  { id: 'babyblue', image: BabyBlueBinder },
  { id: 'blue', image: BlueBinder },
  { id: 'purple', image: PurpleBinder },
];

// Profanity lists for validation
const PROFANITY_LIST = [
  'ass', 'bitch', 'damn', 'hell', 'shit', 'fuck', 'crap', 'penis', 'vagina',
  'sex', 'sexy', 'porn', 'nude', 'naked', 'boobs', 'tits', 'dick', 'cock',
  'pussy', 'slut', 'whore', 'gay', 'lesbian', 'stupid', 'idiot',
  'bastard', 'asshole', 'butthole', 'retard', 'moron', 'dumbass', 'jackass',
  'piss', 'pissed', 'turd', 'fart', 'butt', 'horny', 'kinky', 'rape',
  'murder', 'kill', 'die', 'death', 'blood', 'violence', 'drug', 'drugs',
  'weed', 'cocaine', 'a55', 'b1tch', 'd4mn', 'h3ll', 'sh1t', 'f*ck', 'fck',
  'fcuk', 'fuk', 'p3nis', 'v4gina', 's3x', 'p0rn', 'n00bs', 'b00bs', 't1ts',
  'd1ck', 'c0ck', 'pu55y', '5lut', 'wh0re', 'g4y', 'stpd', '1d10t', 'wtf',
  // Additional words
  'tit', 'azz', 'arse', 'booty', 'boner', 'dildo', 'erect', 'orgasm', 'fetish',
  'stripper', 'hentai', 'milf', 'blowjob', 'handjob', 'cumshot', 'anal',
  'negro', 'nigga', 'nigger', 'chink', 'spic', 'kike', 'faggot', 'fag', 'dyke',
  // More leetspeak
  'b00ty', 'az5', 'ar5e', 't1t', 'f4g', 'd1ld0', 'b0ner', '0rgasm',
  // 25 more
  'cunt', 'twat', 'skank', 'hooker', 'pimp', 'thot', 'hoe', 'tranny',
  'ballsack', 'nutsack', 'jizz', 'sperm', 'semen', 'clit', 'labia',
  'scrotum', 'testicle', 'phallus', 'schlong', 'weiner', 'pecker',
  'queef', 'cameltoe', 'creampie', 'bukkake',
  // Leetspeak variations of added words
  'cvnt', 'cun7', 'tw4t', 'tw@t', 'sk4nk', 'h00ker', 'h0e', 'th0t',
  'b4llsack', 'nut5ack', 'j1zz', 'sp3rm', 's3men', 'cl1t', 'l4bia',
  'scr0tum', 't3sticle', 'ph4llus', 'schl0ng', 'w3iner', 'p3cker',
  'qu33f', 'cam3ltoe', 'cr3ampie', 'bukk4ke', 'tr4nny', 'p1mp',
  // More variations
  'b0n3r', 'd1ld0', '3rect', '0rg4sm', 'f3tish', 'str1pper', 'h3ntai',
  'm1lf', 'bl0wjob', 'h4ndjob', 'cum5hot', '4nal', 'n1gga', 'n1gger',
  'ch1nk', 'sp1c', 'k1ke', 'f4ggot', 'dyk3', 'b00b5', 't1t5', 'd1ck5',
  // Phonetic/sound-alike spellings
  'teets', 'teats', 'titties', 'titty', 'bewbs', 'bewbz', 'boobz', 'boobiez',
  'peen', 'penus', 'peenis', 'peniz', 'dikk', 'dicc', 'dik', 'cok', 'cocc',
  'phuck', 'phuk', 'fuk', 'fuq', 'fux', 'effing', 'azs', 'aas', 'ayss',
  'shyt', 'shiit', 'chit', 'shet', 'beech', 'bytch', 'biatch', 'biotch',
  'hoor', 'hoar', 'whoar', 'sloot', 'slutt', 'kawk', 'cahk', 'pussie',
  'pussi', 'puzzy', 'poosie', 'poosi', 'vag', 'vajj', 'vajayjay', 'cooch',
  'kooch', 'kootch', 'coochie', 'phuq', 'fvck', 'secks', 'sexx', 'seks',
  // More phonetic variations of original words
  'bish', 'beyotch', 'dayum', 'daym', 'dammit', 'damnit', 'heck', 'fook',
  'frick', 'frigg', 'friggin', 'peeniz', 'vagene', 'vegana', 'boobies',
  'tiddies', 'tiddy', 'deek', 'dique', 'kok', 'pusi', 'poosay', 'slute',
  'hor', 'hoer', 'basstard', 'basterd', 'azzhole', 'ahole', 'asswipe',
  'retart', 'rtard', 'moran', 'pizz', 'hornee', 'hawny', 'raep', 'kil',
  'keel', 'deth', 'ded', 'coke', 'cocain', 'krak', 'krack', 'meth',
  'heroin', 'heroinn', 'ecstasy', 'mdma', 'lsd', 'shrooms', 'kum', 'cumm',
  'cuming', 'cumming', 'nutting', 'nuttin', 'fapping', 'fapper', 'wanker',
  'wanka', 'tosser', 'bellend', 'knob', 'nob', 'prck', 'prik', 'prick'
];

const BINDER_NAME_MAX_LENGTH = 24;

const containsProfanity = (text) => {
  const lowerText = text.toLowerCase();
  return PROFANITY_LIST.some(word => lowerText.includes(word));
};

// Icons
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AddBinderModal = ({ isOpen, onClose, existingBinders = [], onCreateBinder }) => {
  useHideNavWhile(isOpen);
  const { theme, isDark } = useTheme();
  const [selectedColor, setSelectedColor] = useState('blue');
  const [binderName, setBinderName] = useState('');
  const [nameError, setNameError] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Validate binder name
  const validateName = (name) => {
    if (!name.trim()) {
      return 'Binder name is required';
    }
    if (name.length > BINDER_NAME_MAX_LENGTH) {
      return `Name must be ${BINDER_NAME_MAX_LENGTH} characters or less`;
    }
    if (containsProfanity(name)) {
      return 'Name contains inappropriate content';
    }
    // Said while they type rather than after they tap Create, because a name
    // rejected on submit reads as the app breaking.
    if (isReservedBinderName(name)) {
      return RESERVED_BINDER_NAME_ERROR;
    }
    return null;
  };

  const handleNameChange = (value) => {
    if (value.length <= BINDER_NAME_MAX_LENGTH) {
      setBinderName(value);
      setNameError(validateName(value));
    }
  };

  const canCreate = binderName.trim() && !nameError;

  const handleCreate = async () => {
    const error = validateName(binderName);
    if (error) {
      setNameError(error);
      return;
    }

    setIsCreating(true);
    try {
      await onCreateBinder?.({
        name: binderName.trim(),
        image: selectedColor,
        isPrivate,
      });
      onClose();
    } catch (error) {
      console.error('Error creating binder:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedColor('blue');
      setBinderName('');
      setNameError(null);
      setIsPrivate(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              ...styles.backdrop,
              backgroundColor: theme.overlay,
            }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 1, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 1, y: '100%' }}
            transition={{ type: 'spring', damping: 34, stiffness: 380, mass: 0.9 }}
            style={{
              ...styles.modal,
              backgroundColor: theme.bgSheet,
            }}
          >
            {/* Handle */}
            <div style={styles.handle} />

            {/* Header */}
            <div style={styles.header}>
              <h2 style={{ ...styles.title, color: theme.text }}>Create Binder</h2>
              <button
                style={{
                  ...styles.closeButton,
                  backgroundColor: theme.bgCard,
                  borderColor: theme.border,
                  color: theme.textSecondary,
                }}
                onClick={onClose}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Binder Name Input */}
            <div style={styles.section}>
              <label style={{ ...styles.label, color: theme.textMuted }}>Binder Name</label>
              <div style={styles.inputContainer}>
                <input
                  type="text"
                  value={binderName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Enter binder name..."
                  style={{
                    ...styles.textInput,
                    backgroundColor: theme.bgCard,
                    borderColor: nameError ? theme.error : theme.border,
                    color: theme.text,
                  }}
                  maxLength={BINDER_NAME_MAX_LENGTH}
                />
                {binderName && (
                  <button
                    style={{
                      ...styles.clearButton,
                      color: theme.textSecondary,
                    }}
                    onClick={() => {
                      setBinderName('');
                      setNameError(null);
                    }}
                  >
                    <CloseIcon />
                  </button>
                )}
                <span style={{ ...styles.charCount, color: theme.textSecondary }}>
                  {binderName.length}/{BINDER_NAME_MAX_LENGTH}
                </span>
              </div>
              {nameError && (
                <div style={styles.errorContainer}>
                  <p style={{ ...styles.errorText, color: theme.error }}>{nameError}</p>
                  {nameError.includes('inappropriate') && (
                    <button
                      style={styles.viewWarningLink}
                      onClick={() => setShowWarning(true)}
                    >
                      View Warning
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Color Selection - Scrollable */}
            <div style={styles.colorSection}>
              <label style={{ ...styles.label, color: theme.textMuted }}>Choose Color</label>
              <div style={styles.colorScrollContainer}>
                <div style={styles.colorGrid}>
                  {binderOptions.map((option) => (
                    <button
                      key={option.id}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: theme.bgCard,
                        borderColor: selectedColor === option.id ? theme.accent : theme.border,
                        ...(selectedColor === option.id ? {
                          backgroundColor: `${theme.accent}1A`,
                        } : {}),
                      }}
                      onClick={() => setSelectedColor(option.id)}
                    >
                      <img
                        src={option.image}
                        alt={option.id}
                        style={styles.colorImage}
                      />
                      {selectedColor === option.id && (
                        <div style={{ ...styles.colorCheck, backgroundColor: theme.accent, color: '#fff' }}>
                          <CheckIcon />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Privacy Toggle */}
            <div
              style={{
                ...styles.privacyRow,
                backgroundColor: theme.bgCard,
                borderColor: theme.border,
              }}
            >
              <div style={styles.privacyInfo}>
                <span style={{ ...styles.privacyLabel, color: theme.text }}>Private Binder</span>
                <span style={{ ...styles.privacyDesc, color: theme.textSecondary }}>Only you can see this binder</span>
              </div>
              <button
                style={{
                  ...styles.toggle,
                  backgroundColor: isPrivate ? theme.accent : theme.border,
                }}
                onClick={() => setIsPrivate(!isPrivate)}
              >
                <div
                  style={{
                    ...styles.toggleKnob,
                    backgroundColor: theme.text,
                    ...(isPrivate ? styles.toggleKnobActive : {}),
                  }}
                />
              </button>
            </div>

            {/* Create Button */}
            <button
              style={{
                ...styles.createButton,
                backgroundColor: !canCreate || isCreating ? theme.border : theme.accent,
                color: theme.text,
              }}
              onClick={handleCreate}
              disabled={!canCreate || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Binder'}
            </button>
          </motion.div>

          {/* Content Policy Warning Modal */}
          {showWarning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={styles.warningOverlay}
              onClick={() => setShowWarning(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                style={{
                  ...styles.warningModal,
                  backgroundColor: theme.bgCard,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={styles.warningHeader}>
                  <div style={styles.warningIcon}>!</div>
                  <h3 style={styles.warningTitle}>Content Policy Notice</h3>
                </div>

                <div style={styles.warningContent}>
                  <p style={styles.warningParagraph}>
                    Binder names that sound like, look like, or represent inappropriate,
                    offensive, or prohibited content may constitute a violation of CardChase's
                    Terms of Service and Community Guidelines.
                  </p>

                  <p style={styles.warningParagraph}>
                    Our automated content filters are designed to assist users in selecting
                    appropriate names. However, these filters are not comprehensive and do
                    not detect all potentially inappropriate content.
                  </p>

                  <p style={styles.warningParagraph}>
                    <strong>Users are solely responsible</strong> for ensuring their content
                    complies with our policies. Attempts to circumvent content filters through
                    creative spelling, substitutions, or other means will be treated as
                    intentional violations.
                  </p>

                  <div style={styles.warningHighlight}>
                    <p style={styles.warningHighlightText}>
                      Violations may result in content removal, account suspension, or
                      permanent ban at CardChase's sole discretion. All moderation decisions
                      are final.
                    </p>
                  </div>
                </div>

                <button
                  style={{
                    ...styles.warningCloseButton,
                    backgroundColor: theme.border,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                  onClick={() => setShowWarning(false)}
                >
                  I Understand
                </button>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: '80px', // Account for bottom nav
    left: '8px',
    right: '8px',
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    overflow: 'hidden',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: {
    width: '36px',
    height: '4px',
    backgroundColor: '#3a3a3a',
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  section: {
    marginBottom: '16px',
    flexShrink: 0,
  },
  inputContainer: {
    position: 'relative',
  },
  textInput: {
    width: '100%',
    padding: '12px 14px',
    paddingRight: '60px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  clearButton: {
    position: 'absolute',
    right: '50px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  charCount: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '12px',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '6px',
  },
  errorText: {
    fontSize: '12px',
    margin: 0,
  },
  viewWarningLink: {
    background: 'none',
    border: 'none',
    color: '#f59e0b',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  warningOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1010,
    padding: '20px',
  },
  warningModal: {
    borderRadius: '16px',
    padding: '24px',
    maxWidth: '400px',
    width: '100%',
    border: '1px solid #f59e0b',
    boxShadow: '0 0 20px rgba(245, 158, 11, 0.2)',
  },
  warningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  warningIcon: {
    width: '32px',
    height: '32px',
    backgroundColor: '#f59e0b',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#000000',
    fontSize: '18px',
    fontWeight: '700',
  },
  warningTitle: {
    color: '#f59e0b',
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  warningContent: {
    marginBottom: '20px',
  },
  warningParagraph: {
    color: '#d1d5db',
    fontSize: '13px',
    lineHeight: '1.6',
    margin: '0 0 14px 0',
  },
  warningHighlight: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '16px',
  },
  warningHighlightText: {
    color: '#ef4444',
    fontSize: '12px',
    lineHeight: '1.5',
    margin: 0,
    fontWeight: '500',
  },
  warningCloseButton: {
    width: '100%',
    padding: '12px',
    border: '1px solid',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  colorSection: {
    marginBottom: '16px',
    flexShrink: 0,
  },
  colorScrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '8px',
    marginLeft: '-20px',
    marginRight: '-20px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    marginBottom: '10px',
    flexShrink: 0,
  },
  colorGrid: {
    display: 'grid',
    gridTemplateRows: 'repeat(2, 1fr)',
    gridAutoFlow: 'column',
    gridAutoColumns: '70px',
    gap: '10px',
    width: 'max-content',
  },
  colorOption: {
    position: 'relative',
    width: '70px',
    height: '88px',
    border: '2px solid',
    borderRadius: '10px',
    padding: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  colorImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  colorCheck: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  privacyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid',
    marginBottom: '16px',
    flexShrink: 0,
  },
  privacyInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  privacyLabel: {
    fontSize: '14px',
    fontWeight: '500',
  },
  privacyDesc: {
    fontSize: '12px',
  },
  toggle: {
    width: '48px',
    height: '28px',
    borderRadius: '14px',
    border: 'none',
    padding: '2px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  toggleKnob: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    transition: 'transform 0.2s ease',
  },
  toggleKnobActive: {
    transform: 'translateX(20px)',
  },
  createButton: {
    width: '100%',
    padding: '14px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    flexShrink: 0,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
};

export default AddBinderModal;
